import React, { useState, useRef, useEffect } from 'react';
import { playLessonText, playPronunciation, playSequence, stopPronunciation } from '../utils/pronunciation';
import WordAnalyzerCard, { type WordSelection } from './WordAnalyzerCard';
import '../styles/proof-of-quality.css';
import '../styles/varnamala-studio.css';
import { varnamalaLabel } from '../utils/barakhadiPhonetics';
import { getLetterMnemonic } from '../data/varnamalaMnemonics';

import { VARNAMALA_LETTER_GROUPS, getVarnamalaGroupMeta } from '../data/varnamalaGroups';

/** Few visible samples per group; Play all still uses the full VARNAMALA_LETTER_GROUPS row. */
const POQ_SAMPLES_PER_GROUP = (letters: string[], idx: number): string[] =>
  idx === 0 ? letters.slice(0, 4) : letters.slice(0, 3);

export interface ProofOfQualityProps {
  onOpenReader: (lessonId?: string) => void;
  onOpenVarnamala?: () => void;
  onOpenGrammar?: () => void;
  onOpenRegister?: () => void;
}

type PoqTab = 'lessons' | 'audio' | 'analyzer' | 'chapter';
type SubLesson = 'sounds' | 'spoken' | 'sentence' | 'math';

interface AudioTrack {
  id: string;
  tag: string;
  title: string;
  subtitle: string;
  source: string;
  text: string;
  lines: string[];
  translation: string;
}

const SAMPLE_TRACKS: AudioTrack[] = [
  {
    id: 'saraswati',
    tag: 'Sacred Hymn · सरस्वतीवन्दना',
    title: 'Saraswati Vandana (Prayer of Wisdom)',
    subtitle: 'Classic verse from Deepakam Class 7 Invocation',
    source: 'Śārdūlavikrīḍita Meter · Traditional Recitation',
    text: 'या कुन्देन्दुतुषारहारधवला या शुभ्रवस्त्रावृता या वीणावरदण्डमण्डितकरा या श्वेतपद्मासना । या ब्रह्माच्युतशङ्करप्रभृतिभिर्देवैः सदा वन्दिता सा मां पातु सरस्वती भगवती निःशेषजाड्यापहा ॥',
    lines: [
      'या कुन्देन्दुतुषारहारधवला या शुभ्रवस्त्रावृता',
      'या वीणावरदण्डमण्डितकरा या श्वेतपद्मासना',
      'या ब्रह्माच्युतशङ्करप्रभृतिभिर्देवैः सदा वन्दिता',
      'सा मां पातु सरस्वती भगवती निःशेषजाड्यापहा',
    ],
    translation:
      'May Goddess Saraswati, who is fair as the jasmine flower, the moon, or snow; who is adorned in pristine white garments; whose hands grace the sacred veena; who is seated upon a pure white lotus; who is perpetually adored by Brahma, Vishnu, and Shiva; completely dispel all our ignorance and protect us.',
  },
  {
    id: 'rigveda',
    tag: 'Rigveda Hymn · ऋग्वेद-मन्त्रः',
    title: 'Saṅgacchadhvaṃ (Universal Solidarity)',
    subtitle: 'Rigveda 10.191.2 · Opening Hymn of Class 8',
    source: 'Rigvedic Vedic Meter · Collective Harmony',
    text: 'संगच्छध्वं संवदध्वं सं वो मनांसि जानताम् । देवा भागं यथा पूर्वे सञ्जानाना उपासते ॥',
    lines: [
      'संगच्छध्वं संवदध्वम्',
      'सं वो मनांसि जानताम्',
      'देवा भागं यथा पूर्वे',
      'सञ्जानाना उपासते',
    ],
    translation:
      'May we walk together in unity; may we speak in harmony; may our minds comprehend together; just as the enlightened sages of old shared their portions in cooperative fellowship.',
  },
  {
    id: 'pavamana',
    tag: 'Upanishadic Chant · मङ्गलाचरणम्',
    title: 'Asato Mā Sadgamaya (The Path of Light)',
    subtitle: 'Bṛhadāraṇyaka Upaniṣad 1.3.28',
    source: 'Meditative Shanti Chant',
    text: 'ॐ असतो मा सद्गमय । तमसो मा ज्योतिर्गमय । मृत्योर्मा अमृतं गमय । ॐ शान्तिः शान्तिः शान्तिः ॥',
    lines: [
      'ॐ असतो मा सद्गमय',
      'तमसो मा ज्योतिर्गमय',
      'मृत्योर्मा अमृतं गमय',
      'ॐ शान्तिः शान्तिः शान्तिः',
    ],
    translation:
      'Lead me from the unreal to the real; lead me from darkness unto light; lead me from mortality unto immortality. Om peace, peace, peace.',
  },
  {
    id: 'spoken',
    tag: 'Spoken Sanskrit · सरल-सम्भाषणम्',
    title: 'Gurukul Daily Conversation',
    subtitle: 'Natural everyday greetings and questions',
    source: 'Modern Conversational Sanskrit (सरल-संस्कृत-सम्भाषणम्)',
    text: 'नमो नमः भ्रातः! भवतः नाम किम्? मम नाम आनन्दः। भवान् कुत्र गच्छति? अहं शालां प्रति गच्छामि।',
    lines: [
      'नमो नमः भ्रातः !',
      'भवतः नाम किम् ?',
      'मम नाम आनन्दः ।',
      'भवान् कुत्र गच्छति ?',
      'अहं शालां प्रति गच्छामि ।',
    ],
    translation:
      'Hello brother! What is your name? My name is Ananda. Where are you going? I am heading towards the school.',
  },
  {
    id: 'phonetics',
    tag: 'Phonetic Articulation · ध्वनिविज्ञानम्',
    title: 'Vowel Echoes & Conjunct Consonants',
    subtitle: 'High-precision articulation for complex sounds',
    source: 'Pāṇinian Śikṣā (पाणिनीय-शिक्षा)',
    text: 'क्ष ज्ञ त्र श्र ऋ ॠ ऌ कः काः किः कुः',
    lines: [
      'क्ष · ज्ञ · त्र · श्र',
      'ऋ · ॠ · ऌ · ॡ',
      'कः · काः · किः · कुः',
    ],
    translation:
      'Phonetic demonstration of conjunct consonants (kṣa, jña, tra, śra), vocalic liquids (ṛ, ṝ, ḷ), and the resonant visarga (ḥ) vowel echo.',
  },
];


interface Chapter1WordGloss {
  word: string;
  gloss: string;
  grammar: string;
}

const CHAPTER1_GLOSSES: Record<string, Chapter1WordGloss> = {
  'अम्ब': { word: 'अम्ब !', gloss: 'O Mother! (सम्बोधनम्)', grammar: 'अम्बा-शब्दः, सम्बोधन-एकवचनम्' },
  'वयं': { word: 'वयं', gloss: 'We all', grammar: 'अस्मद्-सर्वनाम, प्रथमा-बहुवचनम्' },
  'शालायां': { word: 'शालायां', gloss: 'In school', grammar: 'शाला-स्त्रीलिङ्ग-शब्दः, सप्तमी-एकवचनम् (अधिकरणम्)' },
  'गीतं': { word: 'गीतं', gloss: 'Song', grammar: 'गीत-नपुंसकलिङ्ग-शब्दः, द्वितीया-एकवचनम् (कर्म)' },
  'शृणुमः': { word: 'शृणुमः', gloss: 'We hear / listen', grammar: 'श्रु-धातुः, लट्-लकारः (Present), उत्तमपुरुष-बहुवचनम्' },
  'किन्तु': { word: 'किन्तु', gloss: 'However / But', grammar: 'अव्ययम् (Indeclinable)' },
  'अर्थं': { word: 'अर्थं', gloss: 'Meaning / significance', grammar: 'अर्थ-पुंल्लिङ्ग-शब्दः, द्वितीया-एकवचनम्' },
  'जिज्ञासा': { word: 'जिज्ञासा', gloss: 'Curiosity / desire to know', grammar: 'ज्ञातुम् इच्छा = जिज्ञासा (सन्-प्रत्ययान्तः)' },
  'लिखितवान्': { word: 'लिखितवान्', gloss: 'Wrote (he wrote)', grammar: 'लिख्-धातुः + क्तवतु-प्रत्ययः (Past Active Participle)' },
};

export const ProofOfQuality: React.FC<ProofOfQualityProps> = ({
  onOpenReader,
  onOpenVarnamala,
  onOpenRegister,
}) => {
  const [activeTab, setActiveTab] = useState<PoqTab>('lessons');
  const [activeSubLesson, setActiveSubLesson] = useState<SubLesson>('sounds');
  const [activeTrackIdx, setActiveTrackIdx] = useState<number>(0);
  const [isPlayingAudio, setIsPlayingAudio] = useState<boolean>(false);
  const [speechRate, setSpeechRate] = useState<number>(0.75);
  const [mathBase, setMathBase] = useState<number>(35);
  const [sentenceOrder, setSentenceOrder] = useState<number>(0);
  const [selectedSentenceToken, setSelectedSentenceToken] = useState<string>('रामः');
  const [selectedChapterWord, setSelectedChapterWord] = useState<string | null>('शृणुमः');

  const [deconSelection, setDeconSelection] = useState<WordSelection | null>({ text: 'अग्निः', nonce: 1 });
  const stopAudioRef = useRef<(() => void) | null>(null);

  // Stop any ongoing audio on unmount or tab switch
  useEffect(() => {
    return () => {
      if (stopAudioRef.current) stopAudioRef.current();
      stopPronunciation();
    };
  }, []);

  // Listen for direct requests to jump all the way down to the audio lesson
  useEffect(() => {
    const handleStartAudioLesson = () => {
      setActiveTab('lessons');
      setActiveSubLesson('sounds');
      setTimeout(() => {
        const target = document.getElementById('audio-lesson') || document.getElementById('poq-audio-lesson');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
    };

    window.addEventListener('start-free-audio-lesson', handleStartAudioLesson);
    return () => window.removeEventListener('start-free-audio-lesson', handleStartAudioLesson);
  }, []);

  const handleTabChange = (tab: PoqTab) => {
    if (stopAudioRef.current) stopAudioRef.current();
    stopPronunciation();
    setIsPlayingAudio(false);
    setActiveTab(tab);
  };

  const [playingRowIdx, setPlayingRowIdx] = useState<number | null>(null);
  const stopRowRef = useRef<(() => void) | null>(null);
  const [playingLetter, setPlayingLetter] = useState<string | null>(null);
  useEffect(() => () => { if (stopRowRef.current) stopRowRef.current(); }, []);
  const handlePlayRow = (letters: string[], idx: number) => {
    if (stopRowRef.current) stopRowRef.current();
    stopRowRef.current = null;
    if (playingRowIdx === idx) {
      stopPronunciation();
      setPlayingRowIdx(null);
      setPlayingLetter(null);
      return;
    }
    setPlayingRowIdx(idx);
    // Same letter-audio path as a single tap; highlight the tile being spoken.
    stopRowRef.current = playSequence(letters, {
      gapMs: 320,
      onItem: (_w, _i, src) => setPlayingLetter(letters[src] ?? null),
      onDone: () => {
        stopRowRef.current = null;
        setPlayingRowIdx(null);
        setPlayingLetter(null);
      },
    });
  };

  const handlePlaySound = (text: string) => {
    setPlayingRowIdx(null);
    stopPronunciation();
    playPronunciation(text, { plainDevanagari: true, rate: speechRate });
  };

  const handleToggleTrack = (idx: number) => {
    if (idx !== activeTrackIdx) {
      if (stopAudioRef.current) stopAudioRef.current();
      stopPronunciation();
      setIsPlayingAudio(false);
      setActiveTrackIdx(idx);
    }

    if (isPlayingAudio && idx === activeTrackIdx) {
      if (stopAudioRef.current) stopAudioRef.current();
      stopPronunciation();
      setIsPlayingAudio(false);
      return;
    }

    const track = SAMPLE_TRACKS[idx];
    setIsPlayingAudio(true);
    stopAudioRef.current = playSequence(track.lines, {
      gapMs: 900,
      plainDevanagari: true,
      rate: speechRate,
      onDone: () => {
        setIsPlayingAudio(false);
        stopAudioRef.current = null;
      },
    });
  };

  const selectedTrack = SAMPLE_TRACKS[activeTrackIdx];

  // Sentence reordering simulator
  const sentenceOrders = [
    [
      { dev: 'रामः', role: '1st Case (प्रथमा)', desc: 'Agent / Subject: Rama (the one doing the action)' },
      { dev: 'लङ्कायां', role: '7th Case (सप्तमी)', desc: 'Location / Adhikaraṇa: In Lanka (the place)' },
      { dev: 'बाणेन', role: '3rd Case (तृतीया)', desc: 'Instrument / Karaṇa: With an arrow (the means)' },
      { dev: 'रावणम्', role: '2nd Case (द्वितीया)', desc: 'Object / Karma: Ravana (the recipient of action)' },
      { dev: 'अमारयत्', role: 'Past Verb (लङ्-लकारः)', desc: 'Action: Slew / Defeated (3rd Person Singular)' },
    ],
    [
      { dev: 'बाणेन', role: '3rd Case (तृतीया)', desc: 'Instrument / Karaṇa: With an arrow (the means)' },
      { dev: 'रामः', role: '1st Case (प्रथमा)', desc: 'Agent / Subject: Rama (the one doing the action)' },
      { dev: 'रावणम्', role: '2nd Case (द्वितीया)', desc: 'Object / Karma: Ravana (the recipient of action)' },
      { dev: 'लङ्कायां', role: '7th Case (सप्तमी)', desc: 'Location / Adhikaraṇa: In Lanka (the place)' },
      { dev: 'अमारयत्', role: 'Past Verb (लङ्-लकारः)', desc: 'Action: Slew / Defeated (3rd Person Singular)' },
    ],
    [
      { dev: 'रावणम्', role: '2nd Case (द्वितीया)', desc: 'Object / Karma: Ravana (the recipient of action)' },
      { dev: 'लङ्कायां', role: '7th Case (सप्तमी)', desc: 'Location / Adhikaraṇa: In Lanka (the place)' },
      { dev: 'रामः', role: '1st Case (प्रथमा)', desc: 'Agent / Subject: Rama (the one doing the action)' },
      { dev: 'बाणेन', role: '3rd Case (तृतीया)', desc: 'Instrument / Karaṇa: With an arrow (the means)' },
      { dev: 'अमारयत्', role: 'Past Verb (लङ्-लकारः)', desc: 'Action: Slew / Defeated (3rd Person Singular)' },
    ],
  ];

  const currentTokens = sentenceOrders[sentenceOrder];
  const activeTokenInfo = currentTokens.find((t) => t.dev === selectedSentenceToken) || currentTokens[0];

  // Vedic Math calculation
  const mathLeft = Math.floor(mathBase / 10);
  const mathStep1 = mathLeft * (mathLeft + 1);
  const mathResult = mathStep1 * 100 + 25;

  const openVarnamalaRow = (gIdx: number) => {
    onOpenVarnamala?.();
    const id = `varnamala-row-${gIdx}`;
    let tries = 0;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('varnamala-row--flash');
        window.setTimeout(() => el.classList.remove('varnamala-row--flash'), 1800);
      } else if (tries++ < 40) {
        window.setTimeout(tick, 100);
      }
    };
    window.setTimeout(tick, 150);
  };

  return (
    <section className="poq-section" id="proof-of-quality" aria-label="Proof of Quality - Experience Before You Sign Up">
      <div className="poq-container">
        {/* Header Strip */}
        <div className="poq-header">
          <h2 className="poq-title">
            Experience the Gurukul Platform Before Signing Up
            <span className="poq-title-sa">निःशुल्क-अनुभवः · No Registration Required</span>
          </h2>
          <p className="poq-subtitle">
            Zero commitment. Test interactive micro-lessons, hear authentic native audio, explore our Pāṇinian word analyzer, and read an entire NCERT chapter with full audio right now.
          </p>
          <div className="poq-guarantee-strip">
            <span className="poq-guarantee-item"><span>✓</span> Instant Interactive Audio Practice</span>
            <span className="poq-guarantee-item"><span>✓</span> 100% Free · No Sign-Up Needed</span>
            <span className="poq-guarantee-item"><span>✓</span> Authentic Audio Recitations</span>
            <span className="poq-guarantee-item"><span>✓</span> Complete NCERT Deepakam Chapter 1 Free</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="poq-nav-tabs" role="tablist" id="audio-lesson">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'lessons'}
            className={`poq-tab-btn${activeTab === 'lessons' ? ' active' : ''}`}
            onClick={() => handleTabChange('lessons')}
          >
            <span className="poq-tab-icon">🎓</span>
            <span>Short Demo Lessons</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'audio'}
            className={`poq-tab-btn${activeTab === 'audio' ? ' active' : ''}`}
            onClick={() => handleTabChange('audio')}
          >
            <span className="poq-tab-icon">🎧</span>
            <span>Sample Audio Recitations</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'chapter'}
            className={`poq-tab-btn${activeTab === 'chapter' ? ' active' : ''}`}
            onClick={() => handleTabChange('chapter')}
          >
            <span className="poq-tab-icon">📖</span>
            <span>Complete Free Chapter 1</span>
          </button>
        </div>

        {/* ==================================================================
            TAB 1: SHORT DEMO LESSONS
            ================================================================== */}
        {activeTab === 'lessons' && (
          <div className="poq-panel-body" role="tabpanel">
            <div className="poq-sublesson-chips">
              <button
                type="button"
                className={`poq-sublesson-chip${activeSubLesson === 'sounds' ? ' active' : ''}`}
                onClick={() => setActiveSubLesson('sounds')}
              >
                <span>🔤</span>
                <span>1. Sound Science &amp; Shiva Sutras</span>
              </button>
              <button
                type="button"
                className={`poq-sublesson-chip${activeSubLesson === 'spoken' ? ' active' : ''}`}
                onClick={() => setActiveSubLesson('spoken')}
              >
                <span>💬</span>
                <span>2. Everyday Spoken Phrases</span>
              </button>
              <button
                type="button"
                className={`poq-sublesson-chip${activeSubLesson === 'sentence' ? ' active' : ''}`}
                onClick={() => setActiveSubLesson('sentence')}
              >
                <span>📐</span>
                <span>3. Case Endings &amp; Free Word Order</span>
              </button>
              <button
                type="button"
                className={`poq-sublesson-chip${activeSubLesson === 'math' ? ' active' : ''}`}
                onClick={() => setActiveSubLesson('math')}
              >
                <span>⚡</span>
                <span>4. Vedic Mental Math in 2s</span>
              </button>
            </div>

            {/* Sub-Lesson 1: Sounds of Sanskrit */}
            {activeSubLesson === 'sounds' && (
              <div className="poq-lesson-card" id="poq-audio-lesson">
                <div className="poq-lesson-card-header">
                  <span className="poq-lesson-badge">वर्णमाला · Sample Syllables</span>
                  <h3 className="poq-lesson-title">Every group, a few sounds — Play all recites the full row</h3>
                </div>
                <div className="poq-varnamala-groups">
                  {VARNAMALA_LETTER_GROUPS.map((group, gIdx) => {
                    const meta = getVarnamalaGroupMeta(gIdx);
                    const samples = POQ_SAMPLES_PER_GROUP(group, gIdx);
                    const rowActive = playingRowIdx === gIdx;
                    return (
                      <React.Fragment key={gIdx}>
                      <div className="poq-varnamala-group">
                        <div className="poq-varnamala-group-head">
                          <span className="poq-varnamala-group-title">{meta.title}</span>
                          <button
                            type="button"
                            className={`textbook-playrow-btn${rowActive ? ' textbook-playrow-btn--active' : ''}`}
                            onClick={() => handlePlayRow(group, gIdx)}
                            title={`Play all ${group.length} letters`}
                          >
                            <span className="playrow-icon">{rowActive ? '⏹' : '▶'}</span>
                            <span className="playrow-text">{rowActive ? 'Stop' : `Play all (${group.length})`}</span>
                          </button>
                        </div>
                        <div className="poq-varnamala-teaser">
                          {samples.map((letter) => {
                            const m = getLetterMnemonic(letter);
                            const isPlaying = rowActive && playingLetter === letter;
                            return (
                              <button
                                key={letter}
                                type="button"
                                className={`varnamala-bouncy-card${isPlaying ? ' varnamala-bouncy-card--playing' : ''}`}
                                onClick={() => {
                                  if (stopRowRef.current) stopRowRef.current();
                                  stopRowRef.current = null;
                                  setPlayingRowIdx(null);
                                  setPlayingLetter(null);
                                  stopPronunciation();
                                  playPronunciation(letter);
                                }}
                                title={m ? `Hear ${letter} · ${m.wordSan} (${m.wordEn})` : `Hear ${letter}`}
                              >
                                <span className="v-card-akshara">{letter}</span>
                                <small className="v-card-roman">{varnamalaLabel(letter)}</small>
                              </button>
                            );
                          })}
                        </div>
                        <a
                          href={`/swar/#varnamala-row-${gIdx}`}
                          className="poq-varnamala-row-link"
                          onClick={(e) => {
                            if (onOpenVarnamala) {
                              e.preventDefault();
                              openVarnamalaRow(gIdx);
                            }
                          }}
                        >
                          {`See all ${group.length} · ${meta.title.split(' (')[0]} →`}
                        </a>
                      </div>
                      {gIdx === 0 && (
                        <div className="poq-varnamala-decon-panel">
                          <WordAnalyzerCard
                            selection={deconSelection}
                            onSelectWord={(w) => {
                              setDeconSelection({ text: w, nonce: Date.now() });
                              playLessonText(w);
                            }}
                          />
                        </div>
                      )}
                      </React.Fragment>
                    );
                  })}
                </div>
                <div className="poq-teaser-actions">
                  <button type="button" className="poq-play-shiva-btn" onClick={() => handleTabChange('audio')}>
                    🔊 Full Audio →
                  </button>
                  <a
                    href="/swar/"
                    className="poq-play-shiva-btn poq-full-varnamala-link"
                    style={{ textDecoration: 'none' }}
                    onClick={(e) => {
                      if (onOpenVarnamala) {
                        e.preventDefault();
                        onOpenVarnamala();
                      }
                    }}
                  >
                    More · पूर्ण-वर्णमाला →
                  </a>
                </div>


                {/* Shiva Sutras Sample */}
                <div className="poq-shiva-strip">
                  <div>
                    <span style={{ fontSize: '0.74rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                      14 Shiva Sound Aphorisms (माहेश्वर-सूत्राणि):
                    </span>
                    <div className="poq-shiva-text">१. अइउण् · २. ऋऌक् · ३. एओङ् · ४. ऐऔच्</div>
                  </div>
                  <button
                    type="button"
                    className="poq-play-shiva-btn"
                    onClick={() => handlePlaySound('अ इ उ ण् । ऋ लृ क् । ए ओ ङ् । ऐ औ च्')}
                  >
                    <span>🔊 Recite Shiva Sutras</span>
                  </button>
                </div>
              </div>
            )}

            {/* Sub-Lesson 2: Spoken Sanskrit */}
            {activeSubLesson === 'spoken' && (
              <div className="poq-lesson-card">
                <div className="poq-lesson-card-header">
                  <span className="poq-lesson-badge">सरल-सम्भाषणम् · Spoken Sanskrit</span>
                  <h3 className="poq-lesson-title">Speak Sanskrit Naturally From Day One</h3>
                  <p className="poq-lesson-desc">
                    Listen to natural questions and responses used in everyday conversation. Tap any card to hear native pronunciation:
                  </p>
                </div>

                <div className="poq-dialogue-grid">
                  <div className="poq-dialogue-card">
                    <div>
                      <div className="poq-dialogue-q">नमो नमः ! भवतः नाम किम् ?</div>
                      <div className="poq-dialogue-a">मम नाम आनन्दः ।</div>
                      <div className="poq-dialogue-meaning">
                        “Hello! What is your name?” — “My name is Ananda.”
                      </div>
                    </div>
                    <button
                      type="button"
                      className="poq-dialogue-speaker-btn"
                      onClick={() => handlePlaySound('नमो नमः! भवतः नाम किम्? मम नाम आनन्दः।')}
                    >
                      <span>🔊 Listen &amp; Repeat</span>
                    </button>
                  </div>

                  <div className="poq-dialogue-card">
                    <div>
                      <div className="poq-dialogue-q">कथम् अस्ति ?</div>
                      <div className="poq-dialogue-a">कुशलम् अस्मि । सर्वं सम्यक् अस्ति ।</div>
                      <div className="poq-dialogue-meaning">
                        “How are you?” — “I am well. Everything is going great.”
                      </div>
                    </div>
                    <button
                      type="button"
                      className="poq-dialogue-speaker-btn"
                      onClick={() => handlePlaySound('कथम् अस्ति? कुशलम् अस्मि। सर्वं सम्यक् अस्ति।')}
                    >
                      <span>🔊 Listen &amp; Repeat</span>
                    </button>
                  </div>

                  <div className="poq-dialogue-card">
                    <div>
                      <div className="poq-dialogue-q">कृपया इदं स्वीकरोतु ।</div>
                      <div className="poq-dialogue-a">बहु धन्यवादः ! स्वागतम् !</div>
                      <div className="poq-dialogue-meaning">
                        “Please accept this.” — “Thank you very much! You are welcome!”
                      </div>
                    </div>
                    <button
                      type="button"
                      className="poq-dialogue-speaker-btn"
                      onClick={() => handlePlaySound('कृपया इदं स्वीकरोतु। बहु धन्यवादः! स्वागतम्!')}
                    >
                      <span>🔊 Listen &amp; Repeat</span>
                    </button>
                  </div>

                  <div className="poq-dialogue-card">
                    <div>
                      <div className="poq-dialogue-q">पुनर्मिलामः ! शुभदिनम् !</div>
                      <div className="poq-dialogue-a">भवते अपि शुभदिनम् !</div>
                      <div className="poq-dialogue-meaning">
                        “See you again! Have a good day!” — “Wishing you a wonderful day as well!”
                      </div>
                    </div>
                    <button
                      type="button"
                      className="poq-dialogue-speaker-btn"
                      onClick={() => handlePlaySound('पुनर्मिलामः! शुभदिनम्! भवते अपि शुभदिनम्!')}
                    >
                      <span>🔊 Listen &amp; Repeat</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Sub-Lesson 3: Sentence Grammar & Case Endings */}
            {activeSubLesson === 'sentence' && (
              <div className="poq-lesson-card">
                <div className="poq-lesson-card-header">
                  <span className="poq-lesson-badge">कारक-विभक्तिः · Sentence Architecture</span>
                  <h3 className="poq-lesson-title">Why Sanskrit Word Order Is 100% Free</h3>
                  <p className="poq-lesson-desc">
                    In English, <em>“Dog bites man”</em> is opposite to <em>“Man bites dog”</em>. In Sanskrit, case suffixes (Vibhaktis) encode who did what, so words can appear in ANY order without changing the meaning!
                  </p>
                </div>

                <div className="poq-sentence-builder">
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#64748b', marginBottom: '0.65rem' }}>
                    Click any token below to inspect its grammatical role:
                  </div>
                  <div className="poq-sentence-tokens-row">
                    {currentTokens.map((t) => (
                      <div
                        key={t.dev}
                        className={`poq-word-token${selectedSentenceToken === t.dev ? ' selected' : ''}`}
                        onClick={() => {
                          setSelectedSentenceToken(t.dev);
                          handlePlaySound(t.dev);
                        }}
                      >
                        <span className="poq-token-devanagari">{t.dev}</span>
                        <span className="poq-token-case">{t.role.split(' ')[0]}</span>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="poq-sound-btn"
                      style={{ background: '#15803d', color: '#fff', borderColor: '#15803d', padding: '0.6rem 1rem' }}
                      onClick={() => handlePlaySound(currentTokens.map((t) => t.dev).join(' '))}
                    >
                      <span>🔊 Play Sentence</span>
                    </button>
                  </div>

                  <div className="poq-token-detail-card">
                    <div className="poq-td-role">{activeTokenInfo.dev} — {activeTokenInfo.role}</div>
                    <p className="poq-td-desc">{activeTokenInfo.desc}</p>
                  </div>

                  <div className="poq-free-order-demo">
                    <span>
                      <strong>Interactive Test:</strong> Shuffle the word order and see that the meaning remains completely unchanged:
                    </span>
                    <button
                      type="button"
                      className="poq-shuffle-btn"
                      onClick={() => setSentenceOrder((prev) => (prev + 1) % sentenceOrders.length)}
                    >
                      🔀 Shuffle Order (Layout {sentenceOrder + 1}/3)
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Sub-Lesson 4: Vedic Mental Math */}
            {activeSubLesson === 'math' && (
              <div className="poq-lesson-card">
                <div className="poq-lesson-card-header">
                  <span className="poq-lesson-badge">वैदिक-गणितम् · Mental Math in 2 Seconds</span>
                  <h3 className="poq-lesson-title">Sutra 1: एकाधिकेन पूर्वेण (By One More Than the Previous)</h3>
                  <p className="poq-lesson-desc">
                    Square any two-digit number ending in 5 mentally in seconds: multiply the tens digit by (tens digit + 1), and suffix 25.
                  </p>
                </div>

                <div className="poq-math-playground">
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#64748b', marginBottom: '0.5rem' }}>
                    Select a number to calculate its square instantly:
                  </div>
                  <div className="poq-math-chip-row">
                    {[15, 25, 35, 45, 65, 75, 85, 95, 105].map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`poq-math-chip${mathBase === n ? ' active' : ''}`}
                        onClick={() => setMathBase(n)}
                      >
                        {n}²
                      </button>
                    ))}
                  </div>

                  <div className="poq-math-breakdown-card">
                    <div className="poq-mb-target">{mathBase}² = ?</div>
                    <div className="poq-mb-steps">
                      <div className="poq-mb-step-item">
                        <div className="poq-mb-step-title">Step 1: Left Part</div>
                        <div className="poq-mb-step-val">
                          {mathLeft} × ({mathLeft} + 1) = {mathLeft} × {mathLeft + 1} = <strong>{mathStep1}</strong>
                        </div>
                      </div>
                      <div className="poq-mb-step-item">
                        <div className="poq-mb-step-title">Step 2: Right Part</div>
                        <div className="poq-mb-step-val">5² = <strong>25</strong> (Always ends in 25)</div>
                      </div>
                    </div>
                    <div className="poq-mb-final">
                      Result: {mathStep1} | 25 = <strong>{mathResult}</strong>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================================================================
            TAB 2: SAMPLE AUDIO SHOWCASE
            ================================================================== */}
        {activeTab === 'audio' && (
          <div className="poq-panel-body" role="tabpanel">
            <div className="poq-audio-layout">
              {/* Left Column: Playlist */}
              <div className="poq-audio-playlist">
                {SAMPLE_TRACKS.map((t, idx) => (
                  <div
                    key={t.id}
                    className={`poq-audio-track-item${activeTrackIdx === idx ? ' active' : ''}`}
                    onClick={() => handleToggleTrack(idx)}
                  >
                    <div className="poq-ati-meta">
                      <span className="poq-ati-tag">{t.tag}</span>
                      <div className="poq-ati-title">{t.title}</div>
                      <div className="poq-ati-sub">{t.subtitle}</div>
                    </div>
                    <div className="poq-ati-play-icon">
                      {isPlayingAudio && activeTrackIdx === idx ? '⏸' : '▶'}
                    </div>
                  </div>
                ))}
              </div>

              {/* Right Column: Active Player Console */}
              <div className="poq-audio-player-card">
                <div>
                  <div className="poq-ap-header">
                    <span className="poq-ap-eyebrow">{selectedTrack.tag}</span>
                    <h3 className="poq-ap-title">{selectedTrack.title}</h3>
                    <div className="poq-ap-source">{selectedTrack.source}</div>
                  </div>

                  <div className="poq-ap-verse-box">
                    {selectedTrack.lines.map((line, lIdx) => (
                      <div key={lIdx} style={{ marginBottom: '0.35rem' }}>
                        {line}
                      </div>
                    ))}
                  </div>

                  <div className="poq-ap-translation">
                    <strong>Meaning:</strong> {selectedTrack.translation}
                  </div>
                </div>

                <div className="poq-ap-controls">
                  <button
                    type="button"
                    className="poq-ap-main-btn"
                    onClick={() => handleToggleTrack(activeTrackIdx)}
                  >
                    <span>{isPlayingAudio ? '⏸ Pause Recitation' : '▶ Play Recitation'}</span>
                  </button>

                  <div className="poq-ap-speed-group">
                    <span>Speed:</span>
                    <button
                      type="button"
                      className={`poq-ap-speed-btn${speechRate === 0.68 ? ' active' : ''}`}
                      onClick={() => setSpeechRate(0.68)}
                      title="Slow Vedic Chant Tempo"
                    >
                      0.7x (Chant)
                    </button>
                    <button
                      type="button"
                      className={`poq-ap-speed-btn${speechRate === 0.75 ? ' active' : ''}`}
                      onClick={() => setSpeechRate(0.75)}
                      title="Standard Learning Tempo"
                    >
                      0.75x (Study)
                    </button>
                    <button
                      type="button"
                      className={`poq-ap-speed-btn${speechRate === 0.9 ? ' active' : ''}`}
                      onClick={() => setSpeechRate(0.9)}
                      title="Natural Conversational Tempo"
                    >
                      1.0x (Natural)
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================================================================
            TAB 4: COMPLETE FREE CHAPTER 1 (NCERT DEEPAKAM)
            ================================================================== */}
        {activeTab === 'chapter' && (
          <div className="poq-panel-body" role="tabpanel">
            {/* Free Chapter Value Strip */}
            <div className="poq-free-chapter-banner">
              <div className="poq-fcb-left">
                <span className="poq-fcb-badge">🎁 100% UNRESTRICTED FREE ACCESS</span>
                <h3 className="poq-fcb-title">NCERT Class 7 Chapter 1: वन्दे भारतमातरम्</h3>
                <p className="poq-fcb-desc">
                  Experience the full power of our interactive reader without creating an account or paying a rupee. Line-by-line Sanskrit text, word-by-word sandhi breakdown, native audio, and CBSE question patterns are completely unlocked.
                </p>
                <div className="poq-fcb-perks">
                  <span>✓ Full 15 Paragraphs &amp; Invocations</span>
                  <span>✓ 1-Click Audio for Every Line</span>
                  <span>✓ Trilingual Word Lookup Popovers</span>
                  <span>✓ No Credit Card · No Sign-Up</span>
                </div>
              </div>

              <button
                type="button"
                className="poq-fcb-cta-btn"
                onClick={() => onOpenReader('gsde101')}
                title="Launch full NCERT Deepakam Chapter 1 in Reader"
              >
                <span>📖 Open Complete Free Chapter 1 in Reader ➔</span>
              </button>
            </div>

            {/* In-Page Reader Preview Window */}
            <div className="poq-reader-window">
              <div className="poq-rw-header">
                <div>
                  <span style={{ fontSize: '0.74rem', fontWeight: 800, color: '#b45309', textTransform: 'uppercase' }}>
                    Live Reader Preview · कक्षा ७ - प्रथमः पाठः
                  </span>
                  <div className="poq-rw-title">वन्दे भारतमातरम् (Salutations to Mother India)</div>
                </div>
                <button
                  type="button"
                  className="poq-rw-listen-btn"
                  onClick={() =>
                    handlePlaySound(
                      'अम्ब ! वयं विभिन्नेषु कार्यक्रमेषु, आकाशवाण्यां, शालायां च वन्दे मातरम् इति गीतं बहुत्र शृणुमः। किन्तु अस्य अर्थं न जानीमः।'
                    )
                  }
                >
                  <span>🔊 Recite Opening Dialogue</span>
                </button>
              </div>

              {/* Dialogue Block 1 */}
              <div className="poq-rw-verse-block">
                <div className="poq-rw-verse-tag">Passage 1 · Tap any highlighted word to inspect its meaning:</div>
                <div className="poq-rw-verse-words">
                  {['अम्ब', '!', 'वयं', 'विभिन्नेषु', 'कार्यक्रमेषु', ',', 'आकाशवाण्यां', ',', 'शालायां', 'च', '‘वन्दे', 'मातरम्’', 'इति', 'गीतं', 'बहुत्र', 'शृणुमः', '।', 'किन्तु', 'अस्य', 'अर्थं', 'न', 'जानीमः', '।', 'मातः', '!', 'इदं', 'गीतं', 'कः', 'लिखितवान्', 'इत्यपि', 'मम', 'जिज्ञासा', 'अस्ति', '।'].map((token, wIdx) => {
                    const clean = token.replace(/[!,‘’.“”]/g, '').trim();
                    const hasGloss = Boolean(CHAPTER1_GLOSSES[clean]);
                    return (
                      <span
                        key={wIdx}
                        className={`poq-rw-word-chip${selectedChapterWord === clean ? ' active' : ''}`}
                        style={{ cursor: hasGloss ? 'pointer' : 'default', borderStyle: hasGloss ? 'solid' : 'dashed' }}
                        onClick={() => {
                          if (hasGloss) {
                            setSelectedChapterWord(clean);
                            handlePlaySound(clean);
                          }
                        }}
                      >
                        {token}
                      </span>
                    );
                  })}
                </div>

                {selectedChapterWord && CHAPTER1_GLOSSES[selectedChapterWord] && (
                  <div className="poq-rw-word-gloss">
                    <strong>Word Breakdown: </strong>
                    <span style={{ fontSize: '1rem', fontWeight: 800, color: '#b45309' }}>
                      {CHAPTER1_GLOSSES[selectedChapterWord].word}
                    </span>
                    <span> — Meaning: {CHAPTER1_GLOSSES[selectedChapterWord].gloss}</span>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '0.2rem' }}>
                      Grammar Role: {CHAPTER1_GLOSSES[selectedChapterWord].grammar}
                    </div>
                  </div>
                )}

                <div className="poq-rw-translation" style={{ marginTop: '0.85rem' }}>
                  <strong>Parallel English Translation: </strong>
                  “Mother! In various programs, on the radio, and in school, we hear the song ‘Vande Mataram’ in many places. But we do not know its meaning. Mother! Who wrote this song? This is also my curiosity.”
                </div>
              </div>

              {/* Reader Actions Footer */}
              <div className="poq-rw-actions">
                <button
                  type="button"
                  className="poq-fcb-cta-btn"
                  onClick={() => onOpenReader('gsde101')}
                >
                  <span>📖 Continue Reading Full 15 Passages in Reader ➔</span>
                </button>

                {onOpenVarnamala && (
                  <button
                    type="button"
                    className="poq-rw-secondary-cta"
                    onClick={onOpenVarnamala}
                  >
                    <span>🌱 Or Start from Alphabet (वर्णमाला) ➔</span>
                  </button>
                )}

                {onOpenRegister && (
                  <button
                    type="button"
                    className="poq-fcb-cta-btn"
                    style={{ background: 'linear-gradient(135deg, #b45309 0%, #d97706 100%)' }}
                    onClick={onOpenRegister}
                  >
                    <span>🎁 Start 14-Day Free Access ➔</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProofOfQuality;
